import { useEffect, useState } from 'react';
import { FunctionalArea, Member, Team, TeamMember, TeamMemberRole } from '../../../types/team';
import { User } from '../../messaging/types';
import { teamApi } from '../team-api';
import { teamFunctionalAreas } from '../team-functional-area-list';
import { toast } from 'react-toastify';

export function TeamCreate({ onClose, team, isCreate }: { onClose: () => void, team?: Team, isCreate: boolean }) {
    // const { theme } = useTheme();
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedTeam, setSelectedTeam] = useState<Team | undefined | null>(team);
    const [isCreating, setIsCreating] = useState(isCreate);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState<Team>({
        name: team?.name || '',
        description: team?.name || '',
        members: [] as Member[]
    });

    // Helper function to convert functional area names to IDs
    const convertFunctionalAreaNamesToIds = (namesString: string): string[] => {
        const names = (namesString || "").split(",").map(name => name.trim()).filter(name => name);
        return teamFunctionalAreas
            .filter(fa => names.includes(fa.name))
            .map(fa => fa.id);
    };

    // Helper function to convert backend role to frontend role
    // Backend: LEADER=0, MEMBER=1, SECOND_LEVEL_LEADER=2
    // Frontend: MEMBER=0, LEADER=1, OFFICE_BEARER=2
    const convertBackendRoleToFrontendRole = (backendRole: number): TeamMemberRole => {
        // Backend LEADER (0) → Frontend LEADER (1)
        if (backendRole === 0) return TeamMemberRole.LEADER;
        // Backend MEMBER (1) → Frontend MEMBER (0)
        if (backendRole === 1) return TeamMemberRole.MEMBER;
        // Backend SECOND_LEVEL_LEADER (2) → Frontend OFFICE_BEARER (2)
        if (backendRole === 2) return TeamMemberRole.OFFICE_BEARER;
        // Default fallback
        return TeamMemberRole.MEMBER;
    };

    // Helper function to convert frontend role to backend role
    // Frontend: MEMBER=0, LEADER=1, OFFICE_BEARER=2
    // Backend: LEADER=0, MEMBER=1, SECOND_LEVEL_LEADER=2
    const convertFrontendRoleToBackendRole = (frontendRole: TeamMemberRole): number => {
        // Frontend LEADER (1) → Backend LEADER (0)
        if (frontendRole === TeamMemberRole.LEADER) return 0;
        // Frontend MEMBER (0) → Backend MEMBER (1)
        if (frontendRole === TeamMemberRole.MEMBER) return 1;
        // Frontend OFFICE_BEARER (2) → Backend SECOND_LEVEL_LEADER (2)
        if (frontendRole === TeamMemberRole.OFFICE_BEARER) return 2;
        // Default fallback
        return 1;
    };

    const [teamMembers, setTeamMembers] = useState<TeamMember[]>((team?.members || []).map(m => ({
        userId: m.user.id,
        functionalAreas: convertFunctionalAreaNamesToIds(m.functional_area || ""),
        designation: m.designation,
        role: convertBackendRoleToFrontendRole(m.role)
    })));

    const [showUserModal, setShowUserModal] = useState(false);
    const [userSearch, setUserSearch] = useState('');
    const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
    const [showMemberModal, setShowMemberModal] = useState(false);
    const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
    const [memberFormData, setMemberFormData] = useState<TeamMember>({
        userId: 0,
        role: TeamMemberRole.MEMBER,
        functionalAreas: [],
        designation: ''
    });

    if (team) {

    }
    const [functionalAreas] = useState<FunctionalArea[]>(teamFunctionalAreas);

    const fetchUsers = async () => {
        try {
            const data = await teamApi.getAllUsers();
            setUsers(data);
        } catch (err) {
            console.error('Failed to fetch users:', err);
        }
    };

    useEffect(() => {
        fetchUsers();
        if (team?.members) {
            const members = team.members.map(m => ({
                userId: m.user.id,
                functionalAreas: convertFunctionalAreaNamesToIds(m.functional_area || ""),
                designation: m.designation,
                role: convertBackendRoleToFrontendRole(m.role)
            }));
            setTeamMembers(members as TeamMember[]);
        }
    }, [team]);

    const handleCreateTeam = async () => {
        // Validation
        if (!formData.name || formData.name.trim() === '') {
            toast.error('Team name is required');
            setError('Team name is required');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            const teamData = {
                ...formData,
                name: formData.name.trim(),
                description: formData.description?.trim() || '',
                members: teamMembers.map(m => ({
                    userId: m.userId,
                    functional_area: functionalAreas.filter(fa => m.functionalAreas.includes(fa.id)).map(f => f.name).join(","),
                    designation: m.designation,
                    role: convertFrontendRoleToBackendRole(m.role),
                    role_description: ""
                }))
            };

            const response = await teamApi.createTeam(teamData);

            // Only show success if we got a valid response
            if (response && (response.id || response.data?.id || response.data)) {
                toast.success('Team created successfully!');
                
                setIsCreating(false);
                setFormData({
                    name: '',
                    description: '',
                    members: []
                });
                setTeamMembers([]);
                onClose();
            } else {
                const errorMessage = 'Team creation failed: Invalid response from server';
                setError(errorMessage);
                toast.error(errorMessage);
            }
        } catch (err: any) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to create team. Please try again.';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdateTeam = async (id?: number) => {
        if (!selectedTeam) return;
        if (!id) return;

        // Validation
        if (!formData.name || formData.name.trim() === '') {
            toast.error('Team name is required');
            setError('Team name is required');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            const teamData = {
                ...formData,
                name: formData.name.trim(),
                description: formData.description?.trim() || '',
                members: teamMembers.map(m => ({
                    userId: m.userId,
                    functional_area: functionalAreas.filter(fa => m.functionalAreas.includes(fa.id)).map(f => f.name).join(","),
                    designation: m.designation,
                    role: convertFrontendRoleToBackendRole(m.role),
                    role_description: ""
                }))
            };
            
            const response = await teamApi.updateTeam(id, teamData);

            // Only show success if we got a valid response
            if (response && (response.id || response.data?.id || response.data)) {
                toast.success('Team updated successfully!');
                onClose();
            } else {
                const errorMessage = 'Team update failed: Invalid response from server';
                setError(errorMessage);
                toast.error(errorMessage);
            }
        } catch (err: any) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to update team. Please try again.';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFormChange = (field: keyof Team, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const availableUsers = users.filter(
        (u) => !teamMembers.some(member => member.userId === u.id)
    ).filter(u => u.name.toLowerCase().includes(userSearch.toLowerCase()));

    const handleOpenUserModal = () => {
        setUserSearch('');
        setSelectedUserIds([]);
        setShowUserModal(true);
    };

    const handleAddSelectedUsers = () => {
        const newMembers: TeamMember[] = selectedUserIds.map(userId => ({
            userId,
            role: TeamMemberRole.MEMBER,
            functionalAreas: [],
            designation: ''
        }));
        setTeamMembers(prev => [...prev, ...newMembers]);
        setShowUserModal(false);
    };

    const handleOpenMemberModal = (member?: TeamMember) => {
        if (member) {
            setEditingMember(member);
            setMemberFormData(member);
        } else {
            setEditingMember(null);
            setMemberFormData({
                userId: 0,
                role: TeamMemberRole.MEMBER,
                functionalAreas: [],
                designation: ''
            });
        }
        setShowMemberModal(true);
    };

    const handleSaveMember = () => {
        if (editingMember) {
            // Update existing member
            setTeamMembers(prev => prev.map(m =>
                m.userId === editingMember.userId ? memberFormData : m
            ));
        } else {
            // Add new member
            setTeamMembers(prev => [...prev, memberFormData]);
        }
        setShowMemberModal(false);
    };

    const handleRemoveMember = (userId: number) => {
        setTeamMembers(prev => prev.filter(m => m.userId !== userId));
    };

    const handleFunctionalAreaToggle = (areaId: string) => {
        setMemberFormData(prev => ({
            ...prev,
            functionalAreas: prev.functionalAreas.includes(areaId)
                ? prev.functionalAreas.filter(id => id !== areaId)
                : [...prev.functionalAreas, areaId]
        }));
    };

    const getMemberRoleCount = (role: TeamMemberRole) => {
        return teamMembers.filter(m => m.role === role).length;
    };

    const getUserById = (userId: number) => {
        return users.find(u => u.id === userId);
    };

    const getRoleDisplayName = (role: TeamMemberRole) => {
        switch (role) {
            case TeamMemberRole.LEADER:
                return 'Leader';
            case TeamMemberRole.MEMBER:
                return 'Member';
            case TeamMemberRole.OFFICE_BEARER:
                return 'Office Bearer';
            default:
                return role;
        }
    };

    const getRoleColor = (role: TeamMemberRole) => {
        switch (role) {
            case TeamMemberRole.LEADER:
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case TeamMemberRole.OFFICE_BEARER:
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
            case TeamMemberRole.MEMBER:
                return 'bg-neutral-100 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200';
            default:
                return 'bg-neutral-100 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200';
        }
    };

    if (loading) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose}></div>

            {/* Side Panel */}
            <div className="relative w-full max-w-3xl bg-white dark:bg-gray-800 shadow-xl flex flex-col transition-transform duration-300 ease-out transform translate-x-0 h-full">
                {/* Header */}
                <div className="sticky top-0 bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between z-10">
                    <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                        {isCreating ? 'Create Team' : 'Edit Team'}
                    </h2>
                    <button 
                        onClick={onClose} 
                        className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                        aria-label="Close"
                    >
                        <i className="fa-solid fa-times text-xl"></i>
                    </button>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {error && (
                        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                            <div className="flex items-center gap-2">
                                <i className="fa-solid fa-circle-exclamation text-red-600 dark:text-red-400"></i>
                                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                            </div>
                        </div>
                    )}

                    <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Team Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => {
                                    handleFormChange('name', e.target.value);
                                    setError(null);
                                }}
                                className={`w-full px-4 py-2.5 border rounded-lg bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors ${
                                    error && !formData.name ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
                                }`}
                                placeholder="Enter team name"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Description
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => handleFormChange('description', e.target.value)}
                                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                                rows={4}
                                placeholder="Enter team description"
                            />
                        </div>

                        <div>
                            <div className="flex justify-between items-center mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Team Members
                                </label>
                                <button
                                    type="button"
                                    onClick={handleOpenUserModal}
                                    className="btn-primary flex items-center gap-2 px-4 py-2 text-sm"
                                >
                                    <i className="fa-solid fa-plus"></i>
                                    <span>Add Member</span>
                                </button>
                            </div>

                            {/* Role Summary */}
                            <div className="flex gap-4 mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                <div className="flex items-center gap-2">
                                    <i className="fa-solid fa-crown text-green-600 dark:text-green-400"></i>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Leaders: <span className="font-semibold">{getMemberRoleCount(TeamMemberRole.LEADER)}</span>
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <i className="fa-solid fa-star text-blue-600 dark:text-blue-400"></i>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Office Bearers: <span className="font-semibold">{getMemberRoleCount(TeamMemberRole.OFFICE_BEARER)}</span>
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <i className="fa-solid fa-users text-gray-600 dark:text-gray-400"></i>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Members: <span className="font-semibold">{getMemberRoleCount(TeamMemberRole.MEMBER)}</span>
                                    </span>
                                </div>
                            </div>

                            {/* Team Members List */}
                            {teamMembers.length === 0 ? (
                                <div className="text-center py-12 bg-gray-50 dark:bg-gray-700/30 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
                                    <i className="fa-solid fa-users text-4xl text-gray-400 dark:text-gray-600 mb-3"></i>
                                    <p className="text-gray-500 dark:text-gray-400 font-medium">No members added yet</p>
                                    <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Click "Add Member" to get started</p>
                                </div>
                            ) : (
                                <div className="space-y-3 max-h-96 overflow-y-auto">
                                    {teamMembers.sort((m1, m2) => m1.role > m2.role ? 1 : -1).map(member => {
                                        const user = getUserById(member.userId);
                                        if (!user) return null;

                                        return (
                                            <div key={member.userId} className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 font-semibold">
                                                        {user.name.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm font-semibold text-gray-900 dark:text-white">{user.name}</span>
                                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getRoleColor(member.role)}`}>
                                                                {getRoleDisplayName(member.role)}
                                                            </span>
                                                        </div>
                                                        {member.designation && (
                                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                                {member.designation}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleOpenMemberModal(member)}
                                                        className="px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                                    >
                                                        <i className="fa-solid fa-edit"></i>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveMember(member.userId)}
                                                        className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                    >
                                                        <i className="fa-solid fa-trash"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </form>
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-white dark:bg-gray-800 px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 z-10">
                    <button
                        onClick={() => {
                            setIsCreating(false);
                            setSelectedTeam(null);
                            setTeamMembers([]);
                            setError(null);
                            onClose();
                        }}
                        disabled={isSubmitting}
                        className="btn-primary px-6 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={isCreating ? handleCreateTeam : () => handleUpdateTeam(team?.id)}
                        disabled={isSubmitting}
                        className="btn-primary px-6 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {isSubmitting && (
                            <i className="fa-solid fa-spinner fa-spin"></i>
                        )}
                        {isCreating ? 'Create Team' : 'Update Team'}
                    </button>
                </div>
            </div>

            {/* User Modal */}
            {showUserModal && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-2xl shadow-2xl border border-gray-200 dark:border-gray-700">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Add Team Members</h3>
                            <button 
                                onClick={() => setShowUserModal(false)} 
                                className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                            >
                                <i className="fa-solid fa-times text-xl"></i>
                            </button>
                        </div>
                        <input
                            type="text"
                            placeholder="Search users..."
                            value={userSearch}
                            onChange={e => setUserSearch(e.target.value)}
                            className="w-full mb-4 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                        />
                        <div className="max-h-96 overflow-y-auto mb-4">
                            {availableUsers.length === 0 && (
                                <div className="text-neutral-500 text-sm">No users found.</div>
                            )}
                            <div className="grid gap-2">
                                {availableUsers.map(user => {
                                    const dummyMember = users.find(dm => dm.id === user.id);
                                    return (
                                        <label key={user.id} className="flex items-center gap-3 p-3 border dark:border-neutral-700 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={selectedUserIds.includes(user.id)}
                                                onChange={e => {
                                                    if (e.target.checked) {
                                                        setSelectedUserIds(prev => [...prev, user.id]);
                                                    } else {
                                                        setSelectedUserIds(prev => prev.filter(id => id !== user.id));
                                                    }
                                                }}
                                            />
                                            <div className="flex items-center gap-3 flex-1">
                                                <img
                                                    src={dummyMember?.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.name}`}
                                                    className="w-10 h-10 rounded-full border"
                                                    alt={user.name}
                                                />
                                                <div className="flex-1">
                                                    <div className="font-medium text-black dark:text-white">{user.name}</div>
                                                    <div className="text-sm text-neutral-500 dark:text-neutral-400">{user.phone}</div>
                                                    {dummyMember && (
                                                        <div className="text-xs text-neutral-400 dark:text-neutral-500">
                                                            {"dept"} • {"designation"}
                                                        </div>
                                                    )}
                                                </div>
                                                {dummyMember && (
                                                    <div className="text-right">
                                                        <div className="text-xs text-neutral-500 dark:text-neutral-400">
                                                            {dummyMember.phone}
                                                        </div>
                                                        {/* <div className="text-xs text-neutral-400 dark:text-neutral-500">
                                                            Joined: {dummyMember.joinDate}
                                                        </div> */}
                                                    </div>
                                                )}
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                type="button"
                                onClick={() => setShowUserModal(false)}
                                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleAddSelectedUsers}
                                disabled={selectedUserIds.length === 0}
                                className="btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                <i className="fa-solid fa-plus"></i>
                                <span>Add Selected ({selectedUserIds.length})</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Member Edit Modal */}
            {showMemberModal && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-lg shadow-2xl border border-gray-200 dark:border-gray-700">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                                {editingMember ? 'Edit Member' : 'Add Member'}
                            </h3>
                            <button 
                                onClick={() => setShowMemberModal(false)} 
                                className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                            >
                                <i className="fa-solid fa-times text-xl"></i>
                            </button>
                        </div>

                        <div className="space-y-4">
                            {!editingMember && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Select User <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={memberFormData.userId}
                                        onChange={(e) => setMemberFormData(prev => ({ ...prev, userId: parseInt(e.target.value) }))}
                                        className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                                    >
                                        <option value={0}>Select a user</option>
                                        {availableUsers.map(user => (
                                            <option key={user.id} value={user.id}>{user.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Role
                                </label>
                                <select
                                    value={memberFormData.role}
                                    onChange={(e) => setMemberFormData(prev => ({ ...prev, role: Number(e.target.value) as TeamMemberRole }))}
                                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                                >
                                    <option value={TeamMemberRole.MEMBER}>{getRoleDisplayName(TeamMemberRole.MEMBER)}</option>
                                    <option value={TeamMemberRole.LEADER}>{getRoleDisplayName(TeamMemberRole.LEADER)}</option>
                                    <option value={TeamMemberRole.OFFICE_BEARER}>{getRoleDisplayName(TeamMemberRole.OFFICE_BEARER)}</option>
                                </select>
                                {memberFormData.role === TeamMemberRole.LEADER && getMemberRoleCount(TeamMemberRole.LEADER) > 0 && !editingMember && (
                                    <p className="text-xs text-red-500 dark:text-red-400 mt-2 flex items-center gap-1">
                                        <i className="fa-solid fa-triangle-exclamation"></i>
                                        Warning: Only one leader allowed per team
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Designation
                                </label>
                                <input
                                    type="text"
                                    value={memberFormData.designation || ''}
                                    onChange={(e) => setMemberFormData(prev => ({ ...prev, designation: e.target.value }))}
                                    placeholder="e.g., Senior Developer, Project Manager"
                                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Functional Areas
                                </label>
                                <div className="max-h-40 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-3 bg-gray-50 dark:bg-gray-700/50">
                                    {functionalAreas.map(area => (
                                        <label key={area.id} className="flex items-start gap-3 py-2 cursor-pointer hover:bg-white dark:hover:bg-gray-700 rounded px-2 -mx-2">
                                            <input
                                                type="checkbox"
                                                checked={memberFormData.functionalAreas.includes(area.id)}
                                                onChange={() => handleFunctionalAreaToggle(area.id)}
                                                className="mt-1"
                                            />
                                            <div>
                                                <span className="text-sm font-medium text-gray-900 dark:text-white">{area.name}</span>
                                                {area.description && (
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{area.description}</p>
                                                )}
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                type="button"
                                onClick={() => setShowMemberModal(false)}
                                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveMember}
                                disabled={!memberFormData.userId || (memberFormData.role === TeamMemberRole.LEADER && getMemberRoleCount(TeamMemberRole.LEADER) > 0 && !editingMember)}
                                className="btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {editingMember ? 'Update Member' : 'Add Member'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>

    );
} 